import { BACKEND_URL } from '../../api/config.js';
import { countryLabel } from '../../utils/happeningNowFeed.js';
import { formatRelativeDayLabel, localDayDiff } from '../../utils/relativeDay.js';
import { resolveTickerChannel } from '../../utils/tickerDisplay.js';

const PLATFORM_TONE = {
  twitch: '#5b21b6',
  youtube: '#7f1d1d',
  kick: '#14532d',
  epg: '#1e3a5f',
};

export const TIME_FILTERS = [
  { id: 'live', label: 'Live now' },
  { id: 'upcoming', label: 'Upcoming' },
];

const WINDOW_FILTERS = [
  { id: 'live', label: 'Live now' },
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'later', label: 'Later' },
];

const SOON_MS = 2 * 60 * 60 * 1000;

const WINDOWS = new Set(WINDOW_FILTERS.map((chip) => chip.id));

export const CATEGORY_LABELS = {
  sports: 'Sports',
  concerts: 'Concerts',
  nightlife: 'Nightlife',
  culture: 'Culture',
  free: 'Free',
  other: 'Other',
};

export const PLATFORM_CHIPS = [
  { id: 'twitch', label: 'Twitch' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'kick', label: 'Kick' },
  { id: 'epg', label: 'Live TV' },
];

export function itemKey(item, index) {
  return [item.name, item.channel, item.url, item.platform, index].filter(Boolean).join('|');
}

export function flattenDiscoveryItems(sections) {
  const seen = new Set();
  const items = [];
  for (const section of sections || []) {
    for (const item of section.items || []) {
      if (!item) continue;
      const key = itemKey(item, 0);
      if (seen.has(key)) continue;
      seen.add(key);
      items.push({
        ...item,
        scope: item.scope || section.scope,
        countryCode: item.countryCode || section.countryCode,
        platform: item.platform || section.platform || item.source || 'epg',
        sectionLabel: section.label,
      });
    }
  }
  return items;
}

export function scopesFromSections(sections) {
  const map = new Map();
  for (const section of sections || []) {
    const id = section.scope === 'country'
      ? `country:${String(section.countryCode || 'PT').toUpperCase()}`
      : 'global';
    if (map.has(id)) continue;
    map.set(id, {
      id,
      scope: section.scope === 'country' ? 'country' : 'global',
      countryCode: section.countryCode,
      label: section.scope === 'country'
        ? countryLabel(section.countryCode)
        : 'Worldwide',
    });
  }
  return [...map.values()];
}

export function filterDiscoveryItems(items, { scopeId, time, category, now } = {}) {
  const scoped = (items || []).filter((item) => inScope(item, scopeId));
  const useWindow = payloadUsesWindow(scoped);
  const at = now || new Date();
  return scoped.filter((item) => {
    if (time) {
      if (itemTimeId(item, useWindow, at) !== time) return false;
    }
    if (category && item.category !== category) return false;
    return true;
  });
}

function inScope(item, scopeId) {
  if (!scopeId) return true;
  if (scopeId === 'global' && item.scope === 'country') return false;
  if (scopeId.startsWith('country:')) {
    const code = scopeId.slice('country:'.length);
    if (String(item.countryCode || '').toUpperCase() !== code) return false;
  }
  return true;
}

export function itemHasWindow(item) {
  return typeof item?.window === 'string' && WINDOWS.has(item.window);
}

export function payloadUsesWindow(items) {
  return (items || []).some(itemHasWindow);
}

export function itemStartDate(item) {
  if (!item?.startsAt) return null;
  const date = new Date(item.startsAt);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isEpgItem(item) {
  return String(item?.platform || item?.source || '').toLowerCase() === 'epg';
}

/**
 * Non-live EPG that still starts today.
 * soon: starts in less than 2 hours. next: later today.
 */
export function itemTodayBucket(item, now = new Date()) {
  if (item?.live) return null;
  if (!isEpgItem(item)) return null;
  const start = itemStartDate(item);
  if (!start) return null;
  const at = now instanceof Date ? now : new Date(now);
  if (start.getTime() < at.getTime()) return null;
  if (localDayDiff(start, at) !== 0) return null;
  return start.getTime() - at.getTime() < SOON_MS ? 'soon' : 'next';
}

/** Prefer Today from startsAt. Server `window` for live / week / later. */
export function itemTimeId(item, useWindow, now = new Date()) {
  if (itemTodayBucket(item, now)) return 'today';
  if (useWindow) {
    if (item.window === 'tonight') return null;
    if (itemHasWindow(item)) return item.window;
    return item.live ? 'live' : null;
  }
  return item.live ? 'live' : 'upcoming';
}

export function timeFiltersForItems(items, now = new Date()) {
  const list = items || [];
  const hasToday = list.some((item) => itemTodayBucket(item, now));
  const useWindow = payloadUsesWindow(list) || hasToday;
  const catalog = useWindow ? WINDOW_FILTERS : TIME_FILTERS;
  const next = catalog.filter((chip) => list.some((item) => itemTimeId(item, useWindow, now) === chip.id));
  return next.length ? next : [{ id: 'live', label: 'Live now' }];
}

export function todayRailGroups(items, now = new Date()) {
  const soon = [];
  const next = [];
  for (const item of items || []) {
    const bucket = itemTodayBucket(item, now);
    if (bucket === 'soon') soon.push(item);
    if (bucket === 'next') next.push(item);
  }
  const byStart = (a, b) => {
    const aMs = itemStartDate(a)?.getTime() ?? 0;
    const bMs = itemStartDate(b)?.getTime() ?? 0;
    return aMs - bMs;
  };
  return [
    { id: 'soon', label: 'Soon', items: soon.sort(byStart) },
    { id: 'next', label: 'Next', items: next.sort(byStart) },
  ].filter((group) => group.items.length);
}

export function categoriesFromItems(items) {
  const present = new Set();
  for (const item of items || []) {
    if (CATEGORY_LABELS[item?.category]) present.add(item.category);
  }
  return Object.keys(CATEGORY_LABELS)
    .filter((id) => present.has(id))
    .map((id) => ({ id, label: CATEGORY_LABELS[id] }));
}

export function itemThumbnail(item) {
  return absoluteMediaUrl(item?.thumbnail);
}

/** Channel mark used like results EPG cards. Never a stretched programme still. */
export function itemLogoUrl(item) {
  return absoluteMediaUrl(item?.logo, { allowAssetPath: true });
}

/** Live TV uses logo-in-a-mark; streams use thumbnail as cover. */
export function itemUsesChannelMark(item) {
  return String(item?.platform || item?.source || '').toLowerCase() === 'epg';
}

/** Right-edge “scroll for more” hint: overflow and not already at the end. */
export function railShowsMoreHint(el) {
  if (!el) return false;
  const overflow = el.scrollWidth > el.clientWidth + 4;
  const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 12;
  return overflow && !atEnd;
}

function absoluteMediaUrl(value, { allowAssetPath = false } = {}) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw || /placehold\.co/i.test(raw)) return null;
  if (allowAssetPath && raw.startsWith('/assets/')) {
    const origin = BACKEND_URL || 'https://api.livenow-app.com';
    return `${origin}${raw}`;
  }
  try {
    const url = new URL(raw);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.href;
  } catch {
    return null;
  }
}

function formatItemStart(start, now, dateStyle) {
  const time = start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  if (!dateStyle) return time;
  const day = formatRelativeDayLabel(
    start,
    dateStyle === 'later'
      ? { weekday: 'short', month: 'short', day: 'numeric' }
      : { weekday: 'short' },
    now,
  );
  return day ? `${day} · ${time}` : time;
}

/** Drop viewer/heat strings. Keep channel and a real start time when we have one. */
export function safeItemMeta(item, { now = new Date(), dateStyle } = {}) {
  const channel = resolveTickerChannel(item);
  const raw = String(item.meta || '');
  if (/watching/i.test(raw)) {
    return channel || null;
  }
  if (item.live) return channel || null;
  const start = itemStartDate(item);
  const timeHint = start
    ? formatItemStart(start, now, dateStyle)
    : (raw && !/watching/i.test(raw) && !/^starting soon$/i.test(raw) ? raw : null);
  if (channel && timeHint && timeHint !== channel) {
    return dateStyle ? `${timeHint} · ${channel}` : `${channel} · ${timeHint}`;
  }
  return channel || timeHint || null;
}

export function platformTone(platform) {
  return PLATFORM_TONE[String(platform || '').toLowerCase()] || '#1e293b';
}

/** Remaining start in less than 2 hours. Same window as the Today Soon rail. */
export function itemIsStartingSoon(item, now = new Date()) {
  if (item?.live) return false;
  const start = itemStartDate(item);
  if (!start) return false;
  const at = now instanceof Date ? now : new Date(now);
  const diff = start.getTime() - at.getTime();
  return diff > 0 && diff < SOON_MS;
}

export function statusOf(item, now = new Date()) {
  if (item?.live) return 'live';
  if (itemIsStartingSoon(item, now)) return 'soon';
  return null;
}

/** Only show a count when the payload has a real numeric viewers field. */
export function platformViewerCount(item) {
  if (typeof item?.viewers !== 'number' || !Number.isFinite(item.viewers) || item.viewers <= 0) {
    return null;
  }
  return item.viewers;
}
